import React, { useState, useEffect } from 'react';
import { Table, Input, message, Spin, Select, Row, Col, Button, Modal } from 'antd';
import axios from 'axios';
import NavbarDropshipping from '../navbarDropshipping/navbarDropshipping';
import './inventariocrud.css';

/**
 * Componente para gestionar el inventario de productos.
 * Permite editar y eliminar productos, así como buscar y filtrar por categoría.
 * @component
 */
const SolicitudOrdenes = () => {
    const [productos, setProductos] = useState([]);
    const [approvalRequests, setApprovalRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [approvalModalVisible, setApprovalModalVisible] = useState(false);
    const [approvalModalData, setApprovalModalData] = useState(null);
    const [respModalVisible, setRespModalVisible] = useState(false); // estado para modal de responsabilidad
    const [pendingApproveId, setPendingApproveId] = useState(null); // id en espera
    const [pendingAction, setPendingAction] = useState(null); // 'confirmSale' | 'approveRequest' | 'rejectApproval'
    const [confirmSaleModalVisible, setConfirmSaleModalVisible] = useState(false);
    const [confirmSaleId, setConfirmSaleId] = useState(null);
    const [confirmRejectModalVisible, setConfirmRejectModalVisible] = useState(false);
    const [confirmRejectId, setConfirmRejectId] = useState(null);
    const [confirmApproveModalVisible, setConfirmApproveModalVisible] = useState(false);
    const [confirmApproveId, setConfirmApproveId] = useState(null);

    const activeProducts = productos;

    const fetchProducts = async () => {
        setIsLoading(true);
        try {
            const id = localStorage.getItem('usuario_id');
            const response = await axios.get(
                `https://api.99envios.app/api/solicitudes_pendientes_confirmacion/${id}`
            );
            setProductos(response.data.solicitudes);
        } catch (error) {
            message.error('Error al cargar productos');
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchApprovalRequests = async () => {
        try {
            const id = localStorage.getItem('usuario_id');
            const resp = await axios.get(
                `https://api.99envios.app/api/solicitudes_pendientes_aprobacion/${id}`
            );
            setApprovalRequests(resp.data.solicitudes);
        } catch (error) {
            message.error('Error al cargar solicitudes de aprobación');
            console.error(error);
        }
    };

    const handleConfirmSaleAPI = async (id) => {
        setIsLoading(true);
        setPendingAction('confirmSale');
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `https://api.99envios.app/api/confirmar-venta/${id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            message.success('Venta confirmada');
            fetchProducts();
            fetchApprovalRequests();
        } catch (error) {
            message.error('Error al confirmar venta');
            console.error(error);
        } finally {
            setIsLoading(false);
            setPendingAction(null);
        }
    };

    const openConfirmSaleModal = (id) => {
        setConfirmSaleId(id);
        setConfirmSaleModalVisible(true);
    };
    const confirmSale = () => {
        setConfirmSaleModalVisible(false);
        handleConfirmSaleAPI(confirmSaleId);
        setConfirmSaleId(null);
    };

    const confirmSolicitud = async (customId) => {
        setConfirmApproveModalVisible(false);
        setRespModalVisible(false);
        setIsLoading(true);
        setPendingAction('approveRequest');
        try {
            const token = localStorage.getItem('token');
            // Usar customId si está presente, sino confirmApproveId
            const idToUse = customId || confirmApproveId;
            const resp = await axios.post(
                // cambio de URL a la correcta
                `https://api.99envios.app/api/confirmar-solicitud/${confirmApproveId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setApprovalModalData(resp.data);
            setApprovalModalVisible(true);
            fetchProducts();
            fetchApprovalRequests();
        } catch (error) {
            message.error('Error al aprobar solicitud');
            console.error(error);
        } finally {
            setIsLoading(false);
            setPendingAction(null);
            setConfirmApproveId(null);
            setPendingApproveId(null);
        }
    };

    const openConfirmRejectModal = (id) => {
        setConfirmRejectId(id);
        setConfirmRejectModalVisible(true);
    };
    const confirmReject = async () => {
        setConfirmRejectModalVisible(false);
        setIsLoading(true);
        setPendingAction('rejectApproval');
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `https://99envios.app/api/rechazar-solicitud/${confirmRejectId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            message.success('Solicitud rechazada');
            fetchProducts();
            fetchApprovalRequests();
        } catch (error) {
            message.error('Error al rechazar solicitud');
            console.error(error);
        } finally {
            setIsLoading(false);
            setPendingAction(null);
            setConfirmRejectId(null);
        }
    };

    const handleAcceptRequest = (id) => {
        setConfirmApproveId(id);
        setConfirmApproveModalVisible(true);
    };

    const printConfirmedRequests = async () => {
        const confirmedRequests = approvalRequests.filter(request => request.confirmacion_venta);
        
        if (confirmedRequests.length === 0) {
            message.warning('No hay solicitudes confirmadas para imprimir');
            return;
        }

        setIsLoading(true);
        setPendingAction('printConfirmed');
        
        try {
            const token = localStorage.getItem('token');
            const pdfUrls = [];
            
            // Procesar cada solicitud confirmada
            for (const request of confirmedRequests) {
                try {
                    const response = await axios.post(
                        `https://api.99envios.app/api/confirmar-solicitud/${request.id}`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    if (response.data.orden && response.data.orden.pdf) {
                        pdfUrls.push({
                            id: request.id,
                            pdf: response.data.orden.pdf,
                            numeroOrden: response.data.orden.numero_orden
                        });
                    }
                } catch (error) {
                    console.error(`Error procesando solicitud ${request.id}:`, error);
                    message.warning(`Error procesando solicitud ${request.id}`);
                }
            }

            if (pdfUrls.length === 0) {
                message.error('No se pudieron obtener PDFs para imprimir');
                return;
            }

            // Crear ventana con todos los PDFs para imprimir
            const printWindow = window.open('', '_blank');
            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>PDFs de Solicitudes Confirmadas - ${new Date().toLocaleDateString()}</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 0;
                            padding: 20px;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 20px;
                            border-bottom: 2px solid #333;
                            padding-bottom: 10px;
                        }
                        .header h1 {
                            margin: 0;
                            color: #333;
                            font-size: 24px;
                        }
                        .header p {
                            margin: 5px 0;
                            color: #666;
                        }
                        .pdf-container {
                            margin-bottom: 30px;
                            page-break-after: always;
                        }
                        .pdf-container:last-child {
                            page-break-after: auto;
                        }
                        .pdf-title {
                            background-color: #f5f5f5;
                            padding: 10px;
                            margin-bottom: 10px;
                            border: 1px solid #ddd;
                            font-weight: bold;
                            text-align: center;
                        }
                        embed, iframe {
                            width: 100%;
                            height: 800px;
                            border: 1px solid #ddd;
                        }
                        .loading {
                            text-align: center;
                            padding: 50px;
                            color: #666;
                        }
                        .error {
                            text-align: center;
                            padding: 20px;
                            background-color: #ffebee;
                            color: #c62828;
                            border: 1px solid #e57373;
                            margin: 10px 0;
                        }
                        @media print {
                            body { margin: 0; padding: 0; }
                            .no-print { display: none; }
                            .pdf-container { 
                                page-break-after: always; 
                                margin: 0;
                                padding: 0;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="header no-print">
                        <h1>PDFs de Solicitudes Confirmadas</h1>
                        <p>Fecha de impresión: ${new Date().toLocaleString()}</p>
                        <p>Total de PDFs: ${pdfUrls.length}</p>
                    </div>
                    
                    ${pdfUrls.map((pdfData, index) => `
                        <div class="pdf-container">
                            <div class="pdf-title no-print">
                                Solicitud ID: ${pdfData.id} - Orden: ${pdfData.numeroOrden}
                            </div>
                            <embed 
                                src="${pdfData.pdf}" 
                                type="application/pdf" 
                                onload="this.style.display='block'"
                                onerror="this.style.display='none'; this.nextElementSibling.style.display='block'"
                            />
                            <div class="error" style="display: none;">
                                Error al cargar PDF para solicitud ${pdfData.id}
                                <br><a href="${pdfData.pdf}" target="_blank">Abrir PDF en nueva ventana</a>
                            </div>
                        </div>
                    `).join('')}
                    
                    <script>
                        let loadedPdfs = 0;
                        const totalPdfs = ${pdfUrls.length};
                        
                        // Función para verificar si todos los PDFs están cargados
                        function checkAllLoaded() {
                            loadedPdfs++;
                            if (loadedPdfs >= totalPdfs) {
                                setTimeout(() => {
                                    window.print();
                                }, 1000);
                            }
                        }
                        
                        // Configurar eventos de carga para cada PDF
                        window.onload = function() {
                            const embeds = document.querySelectorAll('embed');
                            embeds.forEach(embed => {
                                embed.onload = checkAllLoaded;
                                embed.onerror = checkAllLoaded;
                            });
                            
                            // Fallback: imprimir después de 5 segundos si no todos cargan
                            setTimeout(() => {
                                if (loadedPdfs < totalPdfs) {
                                    window.print();
                                }
                            }, 5000);
                        };
                    </script>
                </body>
                </html>
            `;
            
            printWindow.document.write(printContent);
            printWindow.document.close();

            message.success(`Se procesaron ${pdfUrls.length} de ${confirmedRequests.length} solicitudes para impresión`);
            
            // Actualizar las tablas después del procesamiento
            await fetchProducts();
            await fetchApprovalRequests();

        } catch (error) {
            message.error('Error al procesar solicitudes para impresión');
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
            setPendingAction(null);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchApprovalRequests();
    }, []);

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 80, align: 'center' },
        { title: 'Fecha', dataIndex: 'created_at', key: 'created_at', width: 180,
          render: date => new Date(date).toLocaleString() },
        { title: 'Destinatario', dataIndex: 'destinatario', key: 'destinatario', width: 200,
          render: d => `${d.nombre} ${d.primerApellido} ${d.segundoApellido}` },
        { title: 'Teléfono', dataIndex: ['destinatario','telefono'], key: 'telefono', width: 120 },
        { title: 'Dirección', dataIndex: ['destinatario','direccion'], key: 'direccion', width: 250 },
        { title: 'Transportadora', dataIndex: ['transportadora','nombre'], key: 'transportadora', width: 150 },
        { title: 'Productos', dataIndex: 'productos', key: 'productos', width: 300,
          render: items => (
            <div>
                {items.map((p, i) => (
                    <div key={i} style={{ marginBottom: 4 }}>
                        <strong>Producto:</strong> {p.IdProducto}
                        {p.IdVariacion && (
                            <> – <strong>Variación:</strong> {p.IdVariacion}</>
                        )}
                        <> – <strong>Valor Declarado:</strong> ${p.valorDeclarado.toLocaleString('es-CO')}</>
                        <> – <strong>Cantidad:</strong> {p.Cantidad}</>
                        <> – <strong>Comisión:</strong> ${p.comision.toLocaleString('es-CO')}</>
                    </div>
                ))}
            </div>
          )
        },
        { title: 'Total Valor', key: 'total_valor', width: 120,
          render: (_, r) =>
            `$${r.productos.reduce((sum,p) => sum + p.valorDeclarado, 0)
              .toLocaleString('es-CO')}` },
        { title: 'Contrapago', dataIndex: 'aplica_contrapago', key: 'aplica_contrapago', width: 100, align: 'center',
          render: v => v ? 'Sí' : 'No' },
        {
            title: 'Confirmación Venta',
            dataIndex: 'confirmacion_venta',
            key: 'confirmacion_venta_col',
            width: 140,
            align: 'center',
            render: v => v ? 'Sí' : 'No'
        },
        { title: 'Observaciones', dataIndex: 'observaciones', key: 'observaciones', ellipsis: true },
        { title: 'Estado', dataIndex: 'confirmacion_venta', key: 'confirmacion_venta', width: 120, align: 'center',
          render: v => v ? 'Confirmado' : 'Pendiente' },
    ];

    const confirmColumn = {
        title: 'Acciones',
        key: 'acciones',
        width: 140,
        align: 'center',
        render: (_, record) =>
            record.confirmacion_venta
                ? 'Ya confirmado'
                : <Button type="primary" size="small" onClick={() => openConfirmSaleModal(record.id)}>Confirmar venta</Button>
    };

    const approvalActionsColumn = {
        title: 'Acciones',
        key: 'acciones_aprobacion',
        width: 160,
        align: 'center',
        render: (_, record) => (
            <>
                <Button
                    type="primary"
                    size="small"
                    style={{ marginRight: 8 }}
                    onClick={() => handleAcceptRequest(record.id)}
                >
                    Aceptar
                </Button>
                <Button
                    danger
                    size="small"
                    onClick={() => openConfirmRejectModal(record.id)}
                >
                    Rechazar
                </Button>
            </>
        )
    };

    if (isLoading) {
        const msgs = {
            approveRequest: 'Se está aprobando la solicitud, por favor espere.',
            confirmSale:   'Se está confirmando la venta, por favor espere.',
            rejectApproval:'Se está rechazando la solicitud, por favor espere.',
            printConfirmed: 'Procesando solicitudes confirmadas y generando PDFs para impresión...'
        };
        return (
            <Modal
                visible={true}
                closable={false}
                footer={null}
                centered
                maskClosable={false}
                maskStyle={{ backgroundColor: 'rgba(0,0,0,0.25)' }} // Fondo más tenue
                bodyStyle={{ textAlign: 'center', padding: 40, background: 'white', borderRadius: 8 }}
            >
                <Spin size="large" style={{ marginBottom: 24 }} />
                <div style={{ fontSize: 18, marginTop: 16 }}>{msgs[pendingAction] || 'Procesando...'}</div>
            </Modal>
        );
    }

    return (
        <>
            <NavbarDropshipping />
            <div className="table-containerC" style={{ fontSize: "13px" }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Input
                            placeholder="Buscar productos"
                            style={{ width: 200, marginRight: 10 }}
                        />
                    </Col>
                </Row>

                <div style={{ marginBottom: 24 }}>
                    <h2 style={{ 
                        marginBottom: 16,
                        fontSize: '18px',
                        fontWeight: 600 
                    }}>Solicitudes Pendientes Confirmación ({activeProducts.length})</h2>
                    
                    <p>
                        En esta tabla se muestran las órdenes que aún no han sido confirmadas por el usuario que las generó.
                    </p>
                    
                    <Table 
                        dataSource={activeProducts} 
                        columns={[ ...columns, confirmColumn ]}
                        rowKey="id" 
                        pagination={{ 
                            pageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '25', '50', '100'],
                            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} items`,
                            size: "small"
                        }}
                        scroll={{ x: 'max-content', y: 'calc(181px + 40vh)' }}
                        size="small"
                        bordered
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    />
                </div>

                <div style={{ marginTop: 32 }}>
                    <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                        <Col>
                            <h2 style={{ 
                                margin: 0,
                                fontSize: '18px',
                                fontWeight: 600 
                            }}>Solicitudes Pendientes Aprobación ({approvalRequests.length})</h2>
                        </Col>
                        <Col>
                            <Button 
                                type="primary" 
                                onClick={printConfirmedRequests}
                                disabled={approvalRequests.filter(r => r.confirmacion_venta).length === 0}
                                style={{
                                    backgroundColor: '#52c41a',
                                    borderColor: '#52c41a'
                                }}
                            >
                                Imprimir Confirmadas ({approvalRequests.filter(r => r.confirmacion_venta).length})
                            </Button>
                        </Col>
                    </Row>

                    <p>
                        En esta tabla se muestran las órdenes que ya han sido confirmadas, listas para que procedas con su confirmación final y la generación de la guía. También tienes la opción de generar la guía sin dicha confirmación, asumiendo la responsabilidad correspondiente.
                    </p>

                    <Table 
                        dataSource={approvalRequests} 
                        columns={[ ...columns, approvalActionsColumn ]} 
                        rowKey="id" 
                        pagination={{ 
                            pageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '25', '50', '100'],
                            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} items`,
                            size: "small"
                        }}
                        scroll={{ x: 'max-content', y: 'calc(181px + 40vh)' }}
                        size="small"
                        bordered
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    />
                </div>
            </div>

            <Modal
                visible={approvalModalVisible}
                title="Resultado de la confirmación"
                onCancel={() => setApprovalModalVisible(false)}
                footer={[
                    <Button
                        key="close"
                        style={{ height: 42 }}
                        onClick={() => {
                            setApprovalModalVisible(false);
                        }}
                    >
                        Aceptar
                    </Button>,
                    approvalModalData?.orden?.pdf && (
                        <Button
                            key="pdf"
                            type="primary"
                            size="small"
                            onClick={() => window.open(approvalModalData.orden.pdf, '_blank')}
                        >
                            Ver PDF
                        </Button>
                    )
                ]}
            >
                {approvalModalData ? (
                    <>
                        <div style={{ 
                            padding: '16px',
                            backgroundColor: '#f6f8ff',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid #e6ebff'
                        }}>
                            <p style={{ 
                                color: '#4CAF50',
                                fontSize: '16px',
                                fontWeight: '500',
                                marginBottom: '16px'
                            }}>{approvalModalData.success}</p>
                            
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: '#7c18ff', marginBottom: '8px' }}>Información de la Orden</h4>
                                <div style={{ 
                                    display: 'grid',
                                    gridTemplateColumns: '140px 1fr',
                                    gap: '8px',
                                    fontSize: '14px'
                                }}>
                                    <span style={{ color: '#666' }}>Mensaje:</span>
                                    <span style={{ fontWeight: '500' }}>{approvalModalData.orden.mensaje}</span>
                                    <span style={{ color: '#666' }}>Número de orden:</span>
                                    <span style={{ fontWeight: '500' }}>{approvalModalData.orden.numero_orden}</span>
                                </div>
                            </div>

                            <div>
                                <h4 style={{ color: '#7c18ff', marginBottom: '8px' }}>Detalles Financieros</h4>
                                <div style={{ 
                                    display: 'grid',
                                    gridTemplateColumns: '140px 1fr',
                                    gap: '8px',
                                    fontSize: '14px'
                                }}>
                                    <span style={{ color: '#666' }}>Valor venta:</span>
                                    <span style={{ fontWeight: '500' }}>${approvalModalData.orden.valor_venta?.toLocaleString('es-CO')}</span>
                                    <span style={{ color: '#666' }}>Valor proveedor:</span>
                                    <span style={{ fontWeight: '500' }}>${approvalModalData.orden.valor_proveedor?.toLocaleString('es-CO')}</span>
                                    <span style={{ color: '#666' }}>Valor envío:</span>
                                    <span style={{ fontWeight: '500' }}>${approvalModalData.orden.valor_envio?.toLocaleString('es-CO')}</span>
                                    <span style={{ color: '#666' }}>Valor comisión:</span>
                                    <span style={{ fontWeight: '500' }}>${approvalModalData.orden.valor_comision?.toLocaleString('es-CO')}</span>
                                    <span style={{ color: '#666' }}>Valor utilidad:</span>
                                    <span style={{ 
                                        fontWeight: '600',
                                        color: '#4CAF50'
                                    }}>${approvalModalData.orden.valor_utilidad?.toLocaleString('es-CO')}</span>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin size="large" />
                    </div>
                )}
            </Modal>

            <Modal
                visible={respModalVisible}
                title="Atención"
                onCancel={() => setRespModalVisible(false)}
                footer={[
                    <Button key="cancel" style={{ height: 42 }} onClick={() => setRespModalVisible(false)}>
                        Cancelar
                    </Button>,
                    <Button key="confirm" type="primary" onClick={() => confirmSolicitud(pendingApproveId)}>
                        Aceptar
                    </Button>
                ]}
            >
                <p>
                    No se ha recibido confirmación de quien generó la orden {pendingApproveId}, la aceptación de la solicitud será bajo su propia responsabilidad
                </p>
            </Modal>

            <Modal
                visible={confirmSaleModalVisible}
                title="Confirmar venta"
                onCancel={() => setConfirmSaleModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setConfirmSaleModalVisible(false)}>Cancelar</Button>,
                    <Button key="ok" type="primary" onClick={confirmSale}>Aceptar</Button>
                ]}
            >
                <p>¿Estás seguro de confirmar la venta de la solicitud ID {confirmSaleId}?</p>
            </Modal>

            <Modal
                visible={confirmRejectModalVisible}
                title="Rechazar solicitud"
                onCancel={() => setConfirmRejectModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setConfirmRejectModalVisible(false)}>Cancelar</Button>,
                    <Button key="ok" type="primary" danger onClick={confirmReject}>Aceptar</Button>
                ]}
            >
                <p>¿Estás seguro de rechazar la solicitud ID {confirmRejectId}?</p>
            </Modal>

            <Modal
                visible={confirmApproveModalVisible}
                title="Aprobar solicitud"
                onCancel={() => setConfirmApproveModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setConfirmApproveModalVisible(false)}>Cancelar</Button>,
                    <Button key="ok" type="primary" onClick={confirmSolicitud}>Aceptar</Button>
                ]}
            >
                <p>¿Estás seguro de aprobar la solicitud ID {confirmApproveId}?</p>
            </Modal>
        </>
    );
};

export default SolicitudOrdenes;